import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, Mail, Lock, User, AlertCircle, Phone } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { api } from '../services/api';

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useApp();
  const returnTo = location.state?.returnTo || '/';

  const [mode, setMode] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', password: '', confirmPassword: '', phone: '' });
  const [restaurant, setRestaurant] = useState(null);

  useEffect(() => {
    api.getRestaurant().then(setRestaurant).catch(console.error);
  }, []);

  const handleChange = (e) => {
    setFormData(p => ({ ...p, [e.target.name]: e.target.value }));
    setError('');
    setWarning('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setWarning('');

    try {
      if (mode === 'register') {
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }

        const result = await api.register({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          phone: formData.phone // ✅ ADD THIS
        });

        // ✅ SHOW YELLOW WARNING STYLE MESSAGE
        setWarning(result.message || 'Account created. Please check your email to verify your account.');

        setMode('login');
        setFormData({
          name: '',
          email: formData.email,
          password: '',
          confirmPassword: '',
          phone: '' // ✅ ADD THIS
        });

        return;
      }

      const result = await api.login({
        email: formData.email,
        password: formData.password
      });

      localStorage.setItem('mj_token', result.token);
      login(result.user);
      navigate(result.user.isAdmin ? '/admin' : returnTo);

    } catch (err) {
      setError(err.response?.data?.detail || 'Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
  try {
    await api.resendVerification({ email: formData.email });
    setError('');
    setWarning('Verification email sent again. Please check your inbox.');
  } catch (err) {
    setWarning('');
    setError(err.response?.data?.detail || err.message || 'Failed to resend email');
  }
};

  return (

    <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <h1 className="text-lg font-bold text-gray-900">{mode === 'login' ? 'Sign In' : 'Create Account'}</h1>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            {restaurant === null ? (
              <div className="w-20 h-20 mx-auto mb-3" />
            ) : restaurant?.logoUrl ? (
              <div className="w-20 h-20 mx-auto mb-3 flex items-center justify-center">
                <img
                  src={restaurant.logoUrl}
                  alt={restaurant.name}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            ) : (
              <div className="w-16 h-16 bg-amber-700 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                <span className="text-white font-bold text-2xl">
                  {restaurant?.name?.charAt(0)?.toUpperCase() || 'R'}
                </span>
              </div>
            )}

            <h2 className="text-2xl font-bold text-gray-900">
              {restaurant?.name || ''}
            </h2>

            <p className="text-gray-500 text-sm mt-1">
              {mode === 'login'
                ? 'Welcome back! Sign in to your account'
                : 'Create an account to start ordering'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-1 bg-gray-100 p-1 rounded-xl mb-6">
            {['login', 'register'].map(m => (
              <button key={m} onClick={() => { setMode(m); setError(''); setWarning(''); }}
                className={`py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 capitalize ${mode === m ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
                {m === 'login' ? 'Sign In' : 'Register'}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">

            {warning && (
              <div className="flex items-center gap-2 text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3">
                <AlertCircle className="w-4 h-4 flex-shrink-0 text-amber-600" />
                <p className="text-sm">{warning}</p>
              </div>
            )}

            {mode === 'register' && (
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" name="name" placeholder="Full name" value={formData.name} onChange={handleChange} required
                  className="w-full border border-gray-200 rounded-xl py-3 pl-11 pr-4 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 bg-white" />
              </div>
            )}

            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="email" name="email" placeholder="Email address" value={formData.email} onChange={handleChange} required
                className="w-full border border-gray-200 rounded-xl py-3 pl-11 pr-4 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 bg-white" />
            </div>

            {mode === 'register' && (
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="tel"
                  name="phone"
                  placeholder="Mobile number"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-200 rounded-xl py-3 pl-11 pr-4 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 bg-white"
                />
              </div>
            )}

            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type={showPassword ? 'text' : 'password'} name="password" placeholder="Password" value={formData.password} onChange={handleChange} required
                className="w-full border border-gray-200 rounded-xl py-3 pl-11 pr-12 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 bg-white" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            {mode === 'login' && (
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-xs text-amber-700 font-semibold hover:underline text-right w-full"
              >
                Forgot password?
              </button>
            )}

            {mode === 'register' && (
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="password" name="confirmPassword" placeholder="Confirm password" value={formData.confirmPassword} onChange={handleChange} required
                  className="w-full border border-gray-200 rounded-xl py-3 pl-11 pr-4 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 bg-white" />
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-xl p-3">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            {error.includes('verify your email') && (
            <button
              type="button"
              onClick={handleResendVerification}
              className="text-sm text-amber-700 font-semibold hover:text-amber-800 mt-2"
            >
              Resend verification email
            </button>
          )}

            <button type="submit" disabled={loading}
              className="w-full bg-gray-900 text-white py-3.5 rounded-xl font-semibold text-base hover:bg-amber-700 transition-colors duration-200 disabled:opacity-60 mt-2">
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {mode === 'login' ? 'Signing in...' : 'Creating account...'}
                </div>
              ) : mode === 'login' ? 'Sign In' : 'Create Account'}
            </button>
          </form>

          <p className="text-center text-xs text-gray-400 mt-6">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
    
  );
}